import { useCallback } from 'react'
import { RiAddLine, RiDeleteBinLine, RiFileCopyLine } from 'react-icons/ri'
import toast from 'react-hot-toast'
import { useEditorStore } from '@/stores/useEditorStore'
import { useProjectStore } from '@/stores/useProjectStore'
import { useCreatePage, useDeletePage, useUpdatePage } from '@/api/usePages'
import styles from './PageStrip.module.css'

export function PageStrip() {
    const { currentPageIndex, setCurrentPageIndex } = useEditorStore()
    const { projectId, pages } = useProjectStore()
    const createPage = useCreatePage()
    const deletePage = useDeletePage()
    const updatePage = useUpdatePage()

    const handleAddPage = useCallback(() => {
        if (!projectId) return
        createPage.mutate(
            { projectId, page_number: pages.length + 1 },
            {
                onSuccess: () => {
                    toast.success('Página agregada')
                    setTimeout(() => {
                        const newIdx = useProjectStore.getState().pages.length - 1
                        if (newIdx >= 0) setCurrentPageIndex(newIdx)
                    }, 500)
                },
                onError: () => toast.error('Error al agregar página'),
            }
        )
    }, [projectId, pages.length, createPage, setCurrentPageIndex])

    const handleDeletePage = useCallback((e: React.MouseEvent, pageId: number, index: number) => {
        e.stopPropagation()
        if (!projectId || pages.length <= 1) return
        if (!confirm(`¿Eliminar página ${index + 1}?`)) return
        deletePage.mutate(
            { projectId, pageId },
            {
                onSuccess: () => {
                    toast.success('Página eliminada')
                    if (currentPageIndex >= pages.length - 1) {
                        setCurrentPageIndex(Math.max(0, currentPageIndex - 1))
                    }
                },
                onError: () => toast.error('Error al eliminar'),
            }
        )
    }, [projectId, pages.length, deletePage, currentPageIndex, setCurrentPageIndex])

    const handleDuplicatePage = useCallback((e: React.MouseEvent, sourceIndex: number) => {
        e.stopPropagation()
        if (!projectId) return
        const sourcePage = pages[sourceIndex]
        if (!sourcePage) return
        createPage.mutate(
            { projectId, page_number: pages.length + 1 },
            {
                onSuccess: (newPage) => {
                    if (sourcePage.canvas_json && Object.keys(sourcePage.canvas_json).length > 0) {
                        updatePage.mutate({ projectId, pageId: newPage.id, canvas_json: sourcePage.canvas_json })
                    }
                    toast.success('Página duplicada')
                },
                onError: () => toast.error('Error al duplicar'),
            }
        )
    }, [projectId, pages, createPage, updatePage])

    return (
        <div className={styles.strip}>
            <div className={styles.pages}>
                {pages.map((page, index) => (
                    <div
                        key={page.id}
                        className={`${styles.page} ${index === currentPageIndex ? styles.active : ''}`}
                        onClick={() => setCurrentPageIndex(index)}
                    >
                        <div className={styles.thumbnail}>
                            <span className={styles.pageNum}>{page.page_number}</span>
                        </div>

                        {/* Hover actions */}
                        <div className={styles.actions}>
                            <button
                                className={styles.actionBtn}
                                onClick={(e) => handleDuplicatePage(e, index)}
                                title="Duplicar"
                            >
                                <RiFileCopyLine />
                            </button>
                            {pages.length > 1 && (
                                <button
                                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                    onClick={(e) => handleDeletePage(e, page.id, index)}
                                    title="Eliminar"
                                >
                                    <RiDeleteBinLine />
                                </button>
                            )}
                        </div>

                        <span className={styles.label}>{index + 1}</span>
                    </div>
                ))}

                {/* Add page button */}
                <button
                    className={styles.addBtn}
                    onClick={handleAddPage}
                    disabled={createPage.isPending}
                    title="Agregar página"
                >
                    <RiAddLine />
                </button>
            </div>
        </div>
    )
}
